'use client';

import { Calendar03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { addDays, endOfWeek, format as formatDate, isValid, startOfWeek } from 'date-fns';
import * as React from 'react';

import { Calendar } from './calendar';
import { cn } from './lib/utils';
import { parseDateInput } from './parse-date-input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type DateRegion = 'EU' | 'US';

/**
 * Region defaults — note that `weekStartsOn` matches the picker convention
 * used by react-day-picker / date-fns: 0 = Sunday, 1 = Monday, … 6 = Saturday.
 */
export const REGION_DEFAULTS: Record<DateRegion, { dateFormat: string; weekStartsOn: 0 | 1 }> = {
  EU: { dateFormat: 'dd/MM/yyyy', weekStartsOn: 1 },
  US: { dateFormat: 'MM/dd/yyyy', weekStartsOn: 0 },
};

export type DateRange = { from: Date; to: Date };

export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface CommonProps {
  region?: DateRegion;
  /** date-fns format string. Defaults to the region's format. */
  dateFormat?: string;
  /**
   * 0 = Sunday … 6 = Saturday. Defaults to the region's first day
   * (EU = Monday, US = Sunday).
   */
  weekStartsOn?: WeekStartsOn;
  /** Number of months shown when the picker is open. Defaults to 2. */
  numberOfMonths?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Auto-focus the text input on mount (useful when mounted as a cell editor). */
  autoFocus?: boolean;
  /**
   * Open the calendar popover immediately on mount. Useful when the picker
   * is rendered as a cell editor so the user sees the full calendar without
   * having to click the trigger. Uncontrolled — subsequent open/close state
   * is still managed internally.
   */
  defaultOpen?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** Override the rendered display text (overrides `dateFormat`). */
  formatValue?: (
    value: Date | DateRange | null,
    ctx: { format: string; region: DateRegion },
  ) => string;
  /** Disable specific dates in the calendar. Forwarded to react-day-picker. */
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
}

export interface SingleDatePickerProps extends CommonProps {
  mode?: 'single';
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
}

export interface RangeDatePickerProps extends CommonProps {
  mode: 'range';
  value?: DateRange | null;
  defaultValue?: DateRange | null;
  onValueChange?: (value: DateRange | null) => void;
}

export interface WeekDatePickerProps extends CommonProps {
  mode: 'week';
  value?: DateRange | null;
  defaultValue?: DateRange | null;
  onValueChange?: (value: DateRange | null) => void;
  /** Controlled count of weeks to span. */
  weeks?: number;
  /** Initial count of weeks when uncontrolled. Defaults to 1. */
  defaultWeeks?: number;
  /** Notified when the user changes the week count. */
  onWeeksChange?: (weeks: number) => void;
  /** Minimum selectable weeks. Defaults to 1. */
  minWeeks?: number;
  /** Maximum selectable weeks. Defaults to 12. */
  maxWeeks?: number;
}

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps | WeekDatePickerProps;

function resolveRegion(props: {
  region?: DateRegion;
  dateFormat?: string;
  weekStartsOn?: WeekStartsOn;
}) {
  const region = props.region ?? 'EU';
  const defaults = REGION_DEFAULTS[region];
  return {
    region,
    format: props.dateFormat ?? defaults.dateFormat,
    weekStartsOn: (props.weekStartsOn ?? defaults.weekStartsOn) as WeekStartsOn,
  };
}

function safeFormat(date: Date | undefined | null, fmt: string): string {
  if (!date || !isValid(date)) return '';
  return formatDate(date, fmt);
}

function formatDisplay(
  value: Date | DateRange | null,
  fmt: string,
  region: DateRegion,
  formatValue?: CommonProps['formatValue'],
): string {
  if (formatValue) return formatValue(value, { format: fmt, region });
  if (!value) return '';
  if (value instanceof Date) return safeFormat(value, fmt);
  const from = safeFormat(value.from, fmt);
  const to = safeFormat(value.to, fmt);
  if (!from && !to) return '';
  return `${from} – ${to}`;
}

/**
 * ENG-837: typed entry accepts numeric shorthand — `15-08` fills in the
 * current year, and separators need not match the display format. See
 * `parseDateInput`.
 */
function tryParseSingle(text: string, fmt: string): Date | null {
  return parseDateInput(text, fmt);
}

function computeWeekRange(anchor: Date, weeks: number, weekStartsOn: WeekStartsOn): DateRange {
  const from = startOfWeek(anchor, { weekStartsOn });
  const lastWeekAnchor = addDays(from, (weeks - 1) * 7);
  const to = endOfWeek(lastWeekAnchor, { weekStartsOn });
  return { from, to };
}

function useControllable<T>(controlled: T | undefined, defaultValue: T): [T, (next: T) => void] {
  const [internal, setInternal] = React.useState<T>(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : internal;
  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
    },
    [isControlled],
  );
  return [value, setValue];
}

function DatePicker(props: DatePickerProps) {
  const {
    region: _r,
    dateFormat: _df,
    weekStartsOn: _wso,
    numberOfMonths = 2,
    placeholder,
    disabled,
    className,
    id,
    name,
    autoFocus,
    defaultOpen,
    formatValue,
    disabledDates,
  } = props;
  const { region, format, weekStartsOn } = resolveRegion(props);
  const mode = props.mode ?? 'single';

  // ── Value (controlled + uncontrolled) ─────────────────────────────
  const defaultSingle = (props as SingleDatePickerProps).defaultValue ?? null;
  const defaultRange = (props as RangeDatePickerProps).defaultValue ?? null;
  const [singleValue, setSingleValueInternal] = useControllable<Date | null>(
    mode === 'single' ? (props as SingleDatePickerProps).value : undefined,
    defaultSingle,
  );
  const [rangeValue, setRangeValueInternal] = useControllable<DateRange | null>(
    mode !== 'single' ? (props as RangeDatePickerProps | WeekDatePickerProps).value : undefined,
    defaultRange,
  );

  // ── Week count (controlled + uncontrolled) ────────────────────────
  const weekProps = mode === 'week' ? (props as WeekDatePickerProps) : undefined;
  const minWeeks = weekProps?.minWeeks ?? 1;
  const maxWeeks = weekProps?.maxWeeks ?? 12;
  const [weeks, setWeeksInternal] = useControllable<number>(
    weekProps?.weeks,
    Math.min(Math.max(weekProps?.defaultWeeks ?? 1, minWeeks), maxWeeks),
  );

  const setWeeks = (next: number) => {
    const clamped = Math.min(Math.max(next, minWeeks), maxWeeks);
    setWeeksInternal(clamped);
    weekProps?.onWeeksChange?.(clamped);
    // Recompute the selected range when weeks change so the visual stays in sync.
    if (mode === 'week' && rangeValue?.from) {
      const nextRange = computeWeekRange(rangeValue.from, clamped, weekStartsOn);
      setRangeValueInternal(nextRange);
      weekProps?.onValueChange?.(nextRange);
    }
  };

  // ── Open / close ─────────────────────────────────────────────────
  const [open, setOpen] = React.useState(defaultOpen ?? false);

  // ── Input draft (typing) — single mode only ──────────────────────
  const displayValue =
    mode === 'single'
      ? formatDisplay(singleValue, format, region, formatValue)
      : formatDisplay(rangeValue, format, region, formatValue);

  const [draft, setDraft] = React.useState<string | null>(null);
  React.useEffect(() => {
    // Reset draft whenever the displayed value changes externally.
    setDraft(null);
  }, [displayValue]);

  const inputValue = draft ?? displayValue;

  const commitDraft = () => {
    if (mode !== 'single' || draft === null) {
      setDraft(null);
      return;
    }
    if (draft.trim() === '') {
      setSingleValueInternal(null);
      (props as SingleDatePickerProps).onValueChange?.(null);
      setDraft(null);
      return;
    }
    const parsed = tryParseSingle(draft, format);
    if (parsed) {
      setSingleValueInternal(parsed);
      (props as SingleDatePickerProps).onValueChange?.(parsed);
    }
    setDraft(null);
  };

  // ── Calendar interaction ─────────────────────────────────────────
  const handleSingleSelect = (next: Date | undefined) => {
    const v = next ?? null;
    setSingleValueInternal(v);
    (props as SingleDatePickerProps).onValueChange?.(v);
    if (v) setOpen(false);
  };

  const handleRangeSelect = (next: import('react-day-picker').DateRange | undefined) => {
    if (!next?.from) {
      setRangeValueInternal(null);
      (props as RangeDatePickerProps).onValueChange?.(null);
      return;
    }
    if (next.to) {
      const value = { from: next.from, to: next.to };
      setRangeValueInternal(value);
      (props as RangeDatePickerProps).onValueChange?.(value);
      setOpen(false);
    } else {
      // Partial range — wait for the second click.
      setRangeValueInternal({ from: next.from, to: next.from });
    }
  };

  const handleWeekDayClick = (day: Date) => {
    const range = computeWeekRange(day, weeks, weekStartsOn);
    setRangeValueInternal(range);
    weekProps?.onValueChange?.(range);
    setOpen(false);
  };

  // ── Picker payload per mode ──────────────────────────────────────
  // `exactOptionalPropertyTypes` requires conditional spreading rather than
  // passing `undefined` for optional fields like `defaultMonth` / `disabled`.
  const baseCommon = {
    numberOfMonths,
    weekStartsOn,
    ...(disabledDates !== undefined ? { disabled: disabledDates } : {}),
  } as const;
  const calendar = (() => {
    if (mode === 'single') {
      return (
        <Calendar
          mode="single"
          selected={singleValue ?? undefined}
          onSelect={handleSingleSelect}
          {...baseCommon}
          {...(singleValue ? { defaultMonth: singleValue } : {})}
        />
      );
    }
    if (mode === 'range') {
      return (
        <Calendar
          mode="range"
          selected={rangeValue ? { from: rangeValue.from, to: rangeValue.to } : undefined}
          onSelect={handleRangeSelect}
          {...baseCommon}
          {...(rangeValue?.from ? { defaultMonth: rangeValue.from } : {})}
        />
      );
    }
    // week mode — present as a range visually but click selects a whole week.
    return (
      <div className="flex flex-col gap-2">
        <WeekCountControl
          weeks={weeks}
          minWeeks={minWeeks}
          maxWeeks={maxWeeks}
          onChange={setWeeks}
        />
        <Calendar
          mode="range"
          selected={rangeValue ? { from: rangeValue.from, to: rangeValue.to } : undefined}
          onDayClick={handleWeekDayClick}
          // `onSelect` is required on range mode but we drive selection via
          // onDayClick to snap to whole weeks. The empty handler keeps the
          // visual selection stable.
          onSelect={() => undefined}
          showWeekNumber
          {...baseCommon}
          {...(rangeValue?.from ? { defaultMonth: rangeValue.from } : {})}
        />
      </div>
    );
  })();

  const inputReadOnly = mode !== 'single';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        data-slot="date-picker"
        data-mode={mode}
        className={cn(
          'border-input bg-transparent focus-within:border-ring focus-within:ring-ring/50 aria-disabled:opacity-50 dark:bg-input/30 inline-flex h-9 w-full min-w-0 items-center rounded-md border shadow-xs transition-[color,box-shadow] focus-within:ring-3',
          className,
        )}
        aria-disabled={disabled || undefined}
      >
        <input
          data-slot="date-picker-input"
          id={id}
          name={name}
          type="text"
          value={inputValue}
          placeholder={placeholder ?? format.toLowerCase()}
          readOnly={inputReadOnly}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          aria-describedby={props['aria-describedby']}
          onChange={(e) => {
            if (mode === 'single') setDraft(e.target.value);
          }}
          onDoubleClick={() => {
            if (!disabled) setOpen(true);
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (mode === 'single' && draft !== null) {
                commitDraft();
              } else {
                setOpen((v) => !v);
              }
            } else if (e.key === 'Escape') {
              if (open) {
                e.preventDefault();
                setOpen(false);
              } else if (draft !== null) {
                setDraft(null);
              }
            }
          }}
          className={cn(
            'h-full w-full min-w-0 flex-1 rounded-md bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed',
            inputReadOnly && 'cursor-pointer',
          )}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            data-slot="date-picker-trigger"
            aria-label="Open date picker"
            disabled={disabled}
            className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 inline-flex h-full w-9 shrink-0 items-center justify-center rounded-r-md transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <HugeiconsIcon icon={Calendar03Icon} className="size-4" aria-hidden />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        data-slot="date-picker-content"
        align="start"
        className="w-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {calendar}
      </PopoverContent>
    </Popover>
  );
}

interface WeekCountControlProps {
  weeks: number;
  minWeeks: number;
  maxWeeks: number;
  onChange: (weeks: number) => void;
}

function WeekCountControl({ weeks, minWeeks, maxWeeks, onChange }: WeekCountControlProps) {
  return (
    <div
      data-slot="date-picker-weeks"
      className="border-foreground/10 flex items-center justify-between gap-2 border-b px-3 py-2"
    >
      <label
        htmlFor="date-picker-weeks-input"
        className="text-foreground/70 text-xs font-medium tracking-wider uppercase"
      >
        Weeks
      </label>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Decrease weeks"
          onClick={() => onChange(weeks - 1)}
          disabled={weeks <= minWeeks}
          className="border-input text-foreground/70 hover:bg-muted inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          −
        </button>
        <input
          id="date-picker-weeks-input"
          type="number"
          min={minWeeks}
          max={maxWeeks}
          value={weeks}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
          className="border-input focus-visible:ring-ring/50 h-6 w-10 rounded-md border bg-transparent px-1 text-center text-xs tabular-nums outline-none focus-visible:ring-3"
        />
        <button
          type="button"
          aria-label="Increase weeks"
          onClick={() => onChange(weeks + 1)}
          disabled={weeks >= maxWeeks}
          className="border-input text-foreground/70 hover:bg-muted inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

export { DatePicker };
